import { useMemo } from "react";
import { useThreadStore } from "@/store";
import { isMessageStreaming } from "../utils/messageUtils";
import { extractToolData } from "../utils/toolDataExtractors";

/**
 * useMessageState - Consolidates message state from multiple sources
 *
 * Manages the complex state logic for message display including:
 * - Merging message data from props and store
 * - Determining effective streaming state
 * - Extracting and organizing tool data
 * - Finding related messages (previous user/assistant)
 * - Computing derived state (hasCompletedToolCalls, effectiveIsStreaming)
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.message - Message object from props
 * @param {boolean} params.isStreaming - Whether the message is streaming
 * @param {Array} params.allMessages - All messages in thread (optional, will use store if not provided)
 * @returns {Object} Consolidated message state
 */
export const useMessageState = ({
  message,
  isStreaming: isStreamingProp = false,
  allMessages: allMessagesProp = [],
  toolData: toolDataProp = null,
}) => {
  // Get current message ID for streaming detection
  const currentMessageId = useThreadStore((state) => state.currentMessageId);

  // Get all messages for finding related messages (use prop if provided, otherwise from store)
  const allMessagesFromStore = useThreadStore((state) => state.messages || []);
  const allMessages =
    allMessagesProp.length > 0 ? allMessagesProp : allMessagesFromStore;

  // Derive isUser from message role (must be before hooks)
  const isUser = message?.role === "user";

  // Subscribe to threadStore for this message's updates (reasoning, tool_calls)
  // Must call hooks before any early returns
  const messageFromStore = useThreadStore((state) => {
    const messages = state.messages || [];
    if (!message?.id) return message;
    return messages.find((m) => m.id === message.id) || message;
  });

  // Merge message data from store and props
  const effectiveMessage = useMemo(() => {
    // Ensure we have a valid message to work with
    if (!message) return null;
    if (!messageFromStore) return message;

    // Check if store message has more recent reasoning content
    const storeReasoningContent =
      messageFromStore?.metadata?.reasoningContent ||
      messageFromStore?.reasoningContent ||
      messageFromStore?.metadata?.reasoning_content;

    const propReasoningContent =
      message?.metadata?.reasoningContent ||
      message?.reasoningContent ||
      message?.metadata?.reasoning_content;

    // Check if store message has tool_calls (which might be updated from canvas save)
    const storeHasToolCalls = messageFromStore?.tool_calls;

    // Determine if we should use store message
    const shouldUseStoreReasoning =
      storeReasoningContent &&
      (!propReasoningContent ||
        storeReasoningContent.length > propReasoningContent.length);

    // Always merge tool_calls from store if available (critical for canvas saves)
    const shouldMergeToolCalls = storeHasToolCalls;

    // If we need to merge any data from store
    if (shouldUseStoreReasoning || shouldMergeToolCalls) {
      return {
        ...message,
        // Merge tool_calls from store (critical for canvas save functionality)
        ...(shouldMergeToolCalls && {
          tool_calls: messageFromStore.tool_calls,
        }),
        metadata: {
          ...message.metadata,
          ...messageFromStore.metadata,
          // Ensure reasoning fields are preserved
          ...(shouldUseStoreReasoning && {
            reasoningContent: storeReasoningContent,
            isReasoningResponse:
              messageFromStore.metadata?.isReasoningResponse ||
              message.metadata?.isReasoningResponse,
            inReasoningBlock:
              messageFromStore.metadata?.inReasoningBlock ||
              message.metadata?.inReasoningBlock,
          }),
        },
        ...(shouldUseStoreReasoning && {
          reasoningContent: storeReasoningContent,
        }),
      };
    }

    return message;
  }, [message, messageFromStore]);

  // Use provided tool data or extract it (for backward compatibility)
  const defaultToolData = {
    hasToolCalls: false,
    toolCalls: [],
    imageData: null,
    webSearchData: null,
    canvasData: null,
    isDeepResearchMessage: false,
    deepResearchData: null,
  };
  const extractedData = toolDataProp
    ? toolDataProp
    : effectiveMessage
      ? extractToolData(effectiveMessage, isUser)
      : defaultToolData;

  const hasToolCalls = extractedData.hasToolCalls;
  const toolCalls = extractedData.toolCalls;
  const dalleImageData = extractedData.imageData;
  const webSearchData = extractedData.webSearchData;
  const canvasData = extractedData.canvasData;
  const isDeepResearchMessage = extractedData.isDeepResearchMessage;
  const deepResearchData = extractedData.deepResearchData;

  // Derive content from effective message
  const content = effectiveMessage?.content || "";

  // Find the previous user message (the query that triggered this response)
  const previousUserMessage = useMemo(() => {
    if (!message || isUser || !effectiveMessage?.id) return null;

    const currentIndex = allMessages.findIndex(
      (m) => m?.id === effectiveMessage.id
    );
    if (currentIndex <= 0) return null;

    // Find the most recent user message before this assistant message
    for (let i = currentIndex - 1; i >= 0; i--) {
      const msg = allMessages[i];
      if (!msg) continue;

      // Check various ways a message could be identified as a user message
      const isAssistantMessage =
        msg.role === "assistant" || msg.type === "assistant";

      if (isAssistantMessage) continue;

      // Check if it looks like a user message
      const isUserMessage =
        msg.role === "user" ||
        msg.type === "user" ||
        msg.role === "human" ||
        (typeof msg.isUser !== "undefined" && msg.isUser === true) ||
        // If no role is set, assume it's a user message (default in most systems)
        (!msg.role && !msg.type);

      // Only return if it's a user message and has content
      if (isUserMessage && msg.content) {
        return msg;
      }
    }
    return null;
  }, [allMessages, effectiveMessage?.id, isUser, message]);

  // Find the previous assistant message (for tool_calls lookup)
  const previousAssistantMessage = useMemo(() => {
    if (!message || isUser || !effectiveMessage?.id) return null;

    const currentIndex = allMessages.findIndex(
      (m) => m?.id === effectiveMessage.id
    );
    if (currentIndex <= 0) return null;

    // Find the most recent assistant message before this one
    for (let i = currentIndex - 1; i >= 0; i--) {
      const msg = allMessages[i];
      if (!msg) continue;

      const isAssistantMessage =
        msg.role === "assistant" || msg.type === "assistant";

      if (isAssistantMessage) {
        return msg;
      }
    }
    return null;
  }, [allMessages, effectiveMessage?.id, isUser, message]);

  // Check if tool calls are completed
  const hasCompletedToolCalls = useMemo(() => {
    if (!message || !effectiveMessage?.tool_calls) {
      return false;
    }

    // Handle string format
    let toolCallsArray = effectiveMessage.tool_calls;
    if (typeof toolCallsArray === "string") {
      try {
        toolCallsArray = JSON.parse(toolCallsArray);
      } catch {
        return false;
      }
    }

    // Check if it's an array with tool calls that have results
    if (!Array.isArray(toolCallsArray) || toolCallsArray.length === 0) {
      return false;
    }

    return toolCallsArray.some((tc) => tc && tc.result);
  }, [effectiveMessage?.tool_calls, message]);

  // Determine if message is actively streaming
  const effectiveIsStreaming = useMemo(() => {
    if (!message) return false;

    // If message has completed tool calls, it's definitely not streaming
    if (hasCompletedToolCalls) {
      return false;
    }

    // Check if message is actively streaming
    const isCurrentlyStreaming = effectiveMessage
      ? isMessageStreaming(effectiveMessage)
      : false;

    // If message has meaningful content and is not the current streaming message,
    // consider it finished (enables buttons even if isStreaming flag is stale)
    const hasContent = content && content.trim().length > 0;
    const isCurrentStreamingMessage =
      effectiveMessage?.id === currentMessageId;

    // If we have content but we're not the current streaming message, consider it done
    if (hasContent && !isCurrentStreamingMessage) {
      return false;
    }

    return isCurrentlyStreaming;
  }, [effectiveMessage, hasCompletedToolCalls, content, currentMessageId, message]);

  // Determine effective web search data (handles both direct and previous message sources)
  const effectiveWebSearchData = useMemo(() => {
    if (!message || isUser) return null;

    // If this message has tool calls, use its web search data
    if (hasToolCalls && webSearchData) {
      return webSearchData;
    }

    // If this message has web search data (e.g., Perplexity citations), use it
    if (!hasToolCalls && webSearchData) {
      return webSearchData;
    }

    return null;
  }, [isUser, hasToolCalls, webSearchData, message]);

  // Determine effective image data (only on messages with tool calls)
  const effectiveImageData = message && !isUser && hasToolCalls ? dalleImageData : null;

  // Determine effective canvas data (only on messages with tool calls)
  const effectiveCanvasData = message && !isUser && hasToolCalls ? canvasData : null;

  // Early return if message is not provided (after all hooks)
  if (!message) {
    return {
      effectiveMessage: null,
      effectiveIsStreaming: false,
      hasToolCalls: false,
      hasCompletedToolCalls: false,
      toolCalls: [],
      previousUserMessage: null,
      previousAssistantMessage: null,
      effectiveWebSearchData: null,
      effectiveImageData: null,
      effectiveCanvasData: null,
      dalleImageData: null,
      webSearchData: null,
      canvasData: null,
      isDeepResearchMessage: false,
      deepResearchData: null,
      effectiveDeepResearchData: null,
      toolCallOutput: null,
    };
  }

  return {
    // Core message state
    effectiveMessage,
    effectiveIsStreaming,
    hasToolCalls,
    hasCompletedToolCalls,
    toolCalls,

    // Related messages
    previousUserMessage,
    previousAssistantMessage,

    // Tool data
    effectiveWebSearchData,
    effectiveImageData,
    effectiveCanvasData,
    dalleImageData,
    webSearchData,
    canvasData,
    isDeepResearchMessage,
    deepResearchData,
    effectiveDeepResearchData: deepResearchData,
    toolCallOutput: null, // Placeholder for future tool call output
  };
};
